import {
	Suspense,
	lazy,
	useCallback,
	useEffect,
	useRef,
	useState
} from 'react';
import { ThemeProvider } from '@emotion/react';

import { EVENTS, STATE_DEFAULTS } from '../lib/utils/constants';
import triggerCustomEvent from '../lib/utils/trigger-custom-event';

import { ApplicationContext } from '../contexts/application.context';
import { BlanketContext, BlanketType } from '../contexts/blanket.context';

import { State } from '../types/state';
import { Criteria } from '../types/criteria';
import { useMediaQuery, BreakPointToken } from '@vaad1st/hook.media-query';

import { BlanketComponent } from '@vaad1st/component.atom.blanket';
import { SearchContainer } from './search-experience.styles';
import SummaryComponent from './summary/summary.component';
import SearchExperienceComponent from './search-experience/search-experience.component';
import CriteriaService from '../lib/utils/criteria.service';
import { config } from '../config/config';

interface SearchExperienceContainerProps {
	state: State;
	criteria?: Criteria;
}

const BOOKING_EDITOR_ID = 'booking-search-editor';

const SearchExperienceContainer = ({
	state,
	criteria
}: SearchExperienceContainerProps) => {
	const isMobile = useMediaQuery(`(max-width: ${BreakPointToken.SmMax}px)`);
	let isCriteriaInValid = false;

	if (criteria?.searchType) {
		const isPlaybackValid =
			CriteriaService.validateCriteriaCanShowPlayback(criteria);
		isCriteriaInValid = !isPlaybackValid;

		if (!Object.hasOwn(state, 'closable')) {
			state.closable = isPlaybackValid;
		}
	}

	const initialOpenState = state?.open ?? STATE_DEFAULTS.OPEN;
	const fullScreen = state?.fullScreen ?? STATE_DEFAULTS.FULLSCREEN;
	const isMobileFullScreen = fullScreen && isMobile;

	const getInitialOpenState = () => {
		if (isMobileFullScreen) {
			return true;
		}

		return isMobile ? false : initialOpenState;
	};

	const resolvedInitialOpen = getInitialOpenState();

	const [application, setApplication] = useState<State>({
		brand: state?.brand || STATE_DEFAULTS.BRAND,
		open: resolvedInitialOpen,
		heading: state?.heading ?? config.heading,
		closable: state?.closable ?? STATE_DEFAULTS.CLOSABLE,
		hideToggle: state?.hideToggle ?? STATE_DEFAULTS.HIDE_TOGGLE,
		fullScreen: fullScreen,
		float: state?.float ?? STATE_DEFAULTS.FLOAT,
		flightDestinationPlaceholder: state?.flightDestinationPlaceholder,
		holidayDestinationPlaceholder: state?.holidayDestinationPlaceholder,
		criteria: criteria
	});

	const [includeSummary, setIncludeSummary] = useState<boolean>(
		isMobileFullScreen ? false : !resolvedInitialOpen
	);
	const [includeExperience, setIncludeExperience] = useState<boolean>(
		isMobileFullScreen ? true : resolvedInitialOpen
	);
	const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);
	const [firstEffect, setFirstEffect] = useState<boolean>(!fullScreen);

	const [blanket, setBlanket] = useState<BlanketType>({
		show: false,
		onClick: null
	});
	const summaryTriggerRef = useRef<HTMLButtonElement>(null);
	const restoreSummaryFocus = useRef(false);

	const handleSummaryOpen = useCallback(() => {
		restoreSummaryFocus.current = true;
	}, []);

	const handleResize = useCallback(() => {
		if (document && window) {
			const documentEl = document.documentElement;
			const { innerHeight, innerWidth } = window;

			// Note that it's not possible to set custom properties using Object.assign,
			// so our only choice is two instances of setProperty
			documentEl.style.setProperty('--calculated-vh', `${innerHeight / 100}px`);
			documentEl.style.setProperty('--calculated-vw', `${innerWidth / 100}px`);
		}
	}, []);

	useEffect(() => {
		const { open } = state;

		if (typeof window !== 'undefined' && window.innerWidth) {
			const breakpoint = BreakPointToken.MdMin;

			if (open && window.innerWidth < breakpoint && !isMobileFullScreen) {
				setApplication((prev) => ({ ...prev, open: false }));
			}
		}

		if (!fullScreen) {
			setShouldAnimate(!open);
		}

		handleResize();

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [isMobileFullScreen, fullScreen, state, handleResize]);

	useEffect(() => {
		const newOpenState = isMobileFullScreen
			? true
			: isMobile
				? false
				: initialOpenState;

		setApplication((prev) => {
			if (
				prev.open === newOpenState &&
				prev.fullScreen === isMobileFullScreen
			) {
				return prev;
			}

			return {
				...prev,
				open: newOpenState,
				fullScreen: isMobileFullScreen
			};
		});
		setBlanket((prev) => (prev.show ? { ...prev, show: false } : prev));
	}, [isMobile, fullScreen, initialOpenState, isMobileFullScreen]);

	useEffect(() => {
		const { open } = application;
		let animationTimer: NodeJS.Timeout;
		let blanketTimer: NodeJS.Timeout;
		const animationTimeout = !isMobileFullScreen && shouldAnimate ? 400 : 0;

		if (open) {
			if (isMobile) {
				triggerCustomEvent(EVENTS.flyoutOpen);
			}

			if (!isMobile && !initialOpenState) {
				blanketTimer = setTimeout(() => {
					setBlanket((prev) => ({ ...prev, show: true }));
				}, 250);
			}

			setIncludeExperience(true);
			animationTimer = setTimeout(
				() => setIncludeSummary(false),
				animationTimeout
			);
		} else {
			triggerCustomEvent(EVENTS.flyoutClose);
			setIncludeSummary(true);
			setBlanket((prev) => ({ ...prev, show: false }));
			animationTimer = setTimeout(
				() => setIncludeExperience(false),
				animationTimeout
			);
		}

		if (firstEffect) {
			setTimeout(() => setShouldAnimate(true), 400);
			setFirstEffect(false);
		}

		return () => {
			clearTimeout(animationTimer);
			clearTimeout(blanketTimer);
		};
	}, [application, shouldAnimate, isMobile, firstEffect, initialOpenState]);

	useEffect(() => {
		if (application.open || !includeSummary || !restoreSummaryFocus.current)
			return;

		const animationFrame = requestAnimationFrame(() => {
			summaryTriggerRef.current?.focus();
			restoreSummaryFocus.current = false;
		});

		return () => cancelAnimationFrame(animationFrame);
	}, [application.open, includeSummary]);

	useEffect(() => {
		/**
		 * If browser back button was used, flush cache
		 * This ensures that user will always see an accurate, up-to-date view based on their state
		 * https://stackoverflow.com/questions/8788802/prevent-safari-loading-from-cache-when-back-button-is-clicked
		 */
		window.addEventListener(
			'pageshow',
			(event) => event.persisted && window.location.reload()
		);
	}, []);

	return (
		<ApplicationContext.Provider value={{ application, setApplication }}>
			<BlanketContext.Provider value={{ blanket, setBlanket }}>
				<SearchContainer
					expanded={!!application?.open}
					initiallyOpen={initialOpenState}
					aria-expanded={application?.open}
					role="region"
					aria-label="Search Panel">
					{includeSummary && (
						<SummaryComponent
							config={config}
							triggerRef={summaryTriggerRef}
							onOpen={handleSummaryOpen}
							controlsId={BOOKING_EDITOR_ID}
						/>
					)}
					{includeExperience && (
						<SearchExperienceComponent
							editorId={BOOKING_EDITOR_ID}
							config={config}
							shouldAnimate={shouldAnimate}
							initialOpenState={initialOpenState}
							focusOnOpen={restoreSummaryFocus.current}
						/>
					)}
					<BlanketComponent
						data-cy="global-blanket-component"
						isVisible={blanket.show}
						blanketClicked={() => {
							if (blanket.onClick) {
								setBlanket((prev) => ({ ...prev, onClick: null }));
								blanket.onClick();
							} else {
								setApplication((prev) => ({ ...prev, open: false }));
								setBlanket((prev) => ({ ...prev, show: false }));
							}
						}}
						controlScroll={false}
					/>
				</SearchContainer>
			</BlanketContext.Provider>
		</ApplicationContext.Provider>
	);
};

export default SearchExperienceContainer;
